import React from 'react';
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { Droppable } from '@hello-pangea/dnd';
import { Delete } from '@mui/icons-material';
import type { InventoryLead } from '@otw/models/leads/inventoryLead';
import InventoryLeadCard from './InventoryLeadCard';

export interface InventoryLeadListProps {
  title: string;
  leads: InventoryLead[];
  status: string;
  color: string;
  getContactName: (contactId: string) => string;
  getCompanyName: (companyId: string) => string;
  onViewDetails?: (lead: InventoryLead) => void;
  onDeleteLead?: (lead: InventoryLead) => void;
  onDeleteColumn?: (status: string) => void;
  isDefaultColumn?: boolean;
  columnName?: string;
}

export const InventoryLeadList: React.FC<InventoryLeadListProps> = ({
  title,
  leads,
  status,
  color,
  getContactName,
  getCompanyName,
  onViewDetails,
  onDeleteLead,
  onDeleteColumn,
  isDefaultColumn = false,
  columnName,
}) => {
  const getLighterColor = (inputColor: string, opacity: number = 0.15): string => {
    if (inputColor.startsWith('#')) {
      return `${inputColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
    }
    if (inputColor.startsWith('rgb')) {
      return inputColor.replace(')', `, ${opacity})`).replace('rgb', 'rgba');
    }
    return `rgba(0, 0, 0, ${opacity})`;
  };

  const backgroundColor = getLighterColor(color);

  return (
    <Box sx={{ height: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          height: '100%',
          minHeight: '600px',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor,
          transition: 'background-color 0.3s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            pb: 1,
            borderBottom: '2px solid',
            borderColor: color,
          }}
        >
          <Typography variant="h6" component="h2" fontWeight="500">
            {title}
          </Typography>

          <Box display="flex" alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {leads.length} {leads.length === 1 ? 'lead' : 'leads'}
            </Typography>

            {!isDefaultColumn && onDeleteColumn && (
              <Tooltip title="Delete Column">
                <IconButton
                  size="small"
                  onClick={() => onDeleteColumn(status)}
                  color="error"
                  sx={{
                    ml: 0.5,
                    '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.08)' },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Droppable droppableId={status}>
          {(provided) => (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{
                flexGrow: 1,
                minHeight: '1000px',
                display: 'flex',
                flexDirection: 'column',
                ...(leads.length === 0
                  ? {
                      border: '2px dashed',
                      borderColor: 'rgba(0,0,0,0.1)',
                      borderRadius: 1,
                      justifyContent: 'center',
                      alignItems: 'center',
                      '&::after': {
                        content: '"Drop leads here"',
                        color: 'text.disabled',
                        fontSize: '0.875rem',
                        fontStyle: 'italic',
                      },
                    }
                  : {}),
              }}
            >
              {leads.map((lead, index) => (
                <InventoryLeadCard
                  key={lead.id}
                  lead={lead}
                  index={index}
                  status={status}
                  contactName={getContactName(lead.contactId)}
                  companyName={getCompanyName(lead.companyId)}
                  columnName={columnName || title}
                  columnColor={color}
                  onViewDetails={onViewDetails}
                  onDelete={onDeleteLead ? () => onDeleteLead(lead) : undefined}
                />
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </Paper>
    </Box>
  );
};
